import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AUTH_SERVICE, TOKEN_SERVICE } from '@core/tokens/auth.tokens';
import { HealthService } from '@core/services/health.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/** When set to true on a request context, the interceptor will NOT logout on 401/403. */
export const SKIP_AUTH_LOGOUT = new HttpContextToken<boolean>(() => false);

/** URLs que NO requieren Bearer token (endpoints públicos de auth). */
const PUBLIC_AUTH_URLS = [
  '/auth/login',
  '/auth/register',
  '/auth/password-reset',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/refresh',
  '/auth/logout',
  '/auth/invitations',
];

/** URLs externas excluidas del interceptor completamente. */
const EXCLUDED_URLS = [
  'nominatim.openstreetmap.org',
  'router.project-osrm.org',
];

/** Códigos de error que indican problemas del servidor. */
const SERVER_ERROR_CODES = [0, 500, 502, 503, 504];

/**
 * Interceptor de autenticación HTTP.
 *
 * Agrega Bearer token a requests protegidas.
 * En 401, cierra sesión (excepto en endpoints de auth públicos).
 *
 * All application-level services are resolved lazily via Injector.get()
 * to prevent NG0200 circular dependency (AuthService → HttpClient → interceptor chain).
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const injector = inject(Injector);

  const isExcluded = EXCLUDED_URLS.some((url) => req.url.includes(url));
  if (isExcluded) {
    return next(req);
  }

  const tokenService = injector.get(TOKEN_SERVICE, null);
  if (!tokenService) {
    return next(req);
  }

  const isPublicAuth = PUBLIC_AUTH_URLS.some((url) => req.url.includes(url));

  let modifiedReq = req;

  if (!isPublicAuth) {
    const token = tokenService.getAccessToken();
    if (token) {
      modifiedReq = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }
  }

  return next(modifiedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (SERVER_ERROR_CODES.includes(error.status)) {
        const healthService = injector.get(HealthService);
        healthService.registerError(req.url, error.status, error.message);
      }

      if (error.status === 401 && !isPublicAuth && !req.context.get(SKIP_AUTH_LOGOUT)) {
        const authService = injector.get(AUTH_SERVICE, null);

        if (!authService) {
          const router = injector.get(Router);
          router.navigate(['/auth/login']);
          return throwError(() => error);
        }

        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return authService.refreshAccessToken().pipe(
            switchMap((newToken) => {
              isRefreshing = false;
              refreshTokenSubject.next(newToken);
              return next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
            }),
            catchError((refreshError) => {
              isRefreshing = false;
              refreshTokenSubject.next(null);
              return throwError(() => refreshError);
            }),
          );
        }

        // Another request already triggered refresh — wait for the new token
        return refreshTokenSubject.pipe(
          filter((token): token is string => token !== null),
          take(1),
          switchMap((token) => next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))),
        );
      }

      if (error.status === 403 && !isPublicAuth && !req.context.get(SKIP_AUTH_LOGOUT)) {
        tokenService.clearTokens();
        const authService = injector.get(AUTH_SERVICE, null);
        if (authService) {
          authService.logout();
        } else {
          const router = injector.get(Router);
          router.navigate(['/auth/login']);
        }
      }

      return throwError(() => error);
    })
  );
};
